import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';

interface DemoModeContextType {
  isDemoMode: boolean;
  setDemoMode: (value: boolean) => void;
  toggleDemoMode: () => void;
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined);

const PRODUCTION_HOSTS = ['linkmedicalai.com', 'www.linkmedicalai.com'];
const DEMO_HOSTS = ['demo.linkmedicalai.com'];

type StoredDemoMode = 'true' | 'false' | null;

interface ResolveInitialDemoModeOptions {
  hostname: string;
  pathname?: string;
  envDemoMode: boolean;
  search: string;
  storedDemoMode: string | null;
}

interface DemoModeDecision {
  isDemoMode: boolean;
  storedDemoMode: StoredDemoMode;
}

export function isPrimaryProductionHost(hostname: string) {
  return PRODUCTION_HOSTS.includes(hostname);
}

export function isDedicatedDemoHost(hostname: string) {
  return DEMO_HOSTS.includes(hostname);
}

export function isSurgeonConsoleHomeHost(hostname: string) {
  return isDedicatedDemoHost(hostname);
}

export function resolveInitialDemoMode({
  hostname,
  pathname = '',
  envDemoMode,
  search,
  storedDemoMode,
}: ResolveInitialDemoModeOptions): DemoModeDecision {
  const urlParams = new URLSearchParams(search);
  const urlDemoOn = urlParams.get('demo') === 'true' || urlParams.get('beta') === 'true';
  const urlDemoOff = urlParams.get('demo') === 'false';
  const isSurgeonRoute = pathname === '/surgeon' || pathname.startsWith('/surgeon/');

  if (isPrimaryProductionHost(hostname)) {
    if (isSurgeonRoute && urlDemoOn) {
      return { isDemoMode: true, storedDemoMode: 'true' };
    }
    return { isDemoMode: false, storedDemoMode: null };
  }

  if (isDedicatedDemoHost(hostname)) {
    return { isDemoMode: true, storedDemoMode: 'true' };
  }

  if (urlDemoOff) {
    return { isDemoMode: false, storedDemoMode: null };
  }

  if (urlDemoOn) {
    return { isDemoMode: true, storedDemoMode: 'true' };
  }

  if (!envDemoMode && storedDemoMode !== 'true') {
    return { isDemoMode: false, storedDemoMode };
  }

  return { isDemoMode: storedDemoMode === 'true', storedDemoMode };
}

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isProductionHost = isPrimaryProductionHost(hostname);
  const isDemoHost = isDedicatedDemoHost(hostname);

  const envDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';

  const [demoModeState, setDemoModeState] = useState<boolean>(() => {
    const canUseStorage = typeof window !== 'undefined';
    let storedDemoMode: string | null = null;

    if (canUseStorage) {
      try {
        storedDemoMode = localStorage.getItem('link_demo_mode');
      } catch {
        storedDemoMode = null;
      }
    }

    const decision = resolveInitialDemoMode({
      hostname,
      pathname: typeof window !== 'undefined' ? window.location.pathname : '',
      envDemoMode,
      search: typeof window !== 'undefined' ? window.location.search : '',
      storedDemoMode,
    });

    if (!canUseStorage) {
      return decision.isDemoMode;
    }

    if (decision.storedDemoMode === null) {
      try {
        localStorage.removeItem('link_demo_mode');
      } catch {
        // ignore storage errors (private mode, etc.)
      }
    } else if (decision.storedDemoMode !== storedDemoMode) {
      try {
        localStorage.setItem('link_demo_mode', decision.storedDemoMode);
      } catch {
        // ignore storage errors (private mode, etc.)
      }
    }

    return decision.isDemoMode;
  });

  const setDemoMode = (value: boolean) => {
    // Primary production is never demo. The demo subdomain is always demo.
    if (isProductionHost || isDemoHost) return;
    setDemoModeState(value);
    try {
      localStorage.setItem('link_demo_mode', value.toString());
    } catch {
      // ignore storage errors (private mode, etc.)
    }
  };

  const toggleDemoMode = () => {
    if (isProductionHost || isDemoHost) return;
    setDemoMode(!demoModeState);
  };

  const value = useMemo(() => ({
    isDemoMode: demoModeState,
    setDemoMode,
    toggleDemoMode,
  }), [demoModeState]);

  return (
    <DemoModeContext.Provider value={value}>
      {children}
    </DemoModeContext.Provider>
  );
}

export function useDemoMode() {
  const context = useContext(DemoModeContext);
  if (!context) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
}
